import React, { useEffect, useState } from 'react';
import { Button, Col, Form, Pagination, Row, Spinner } from 'react-bootstrap';
import CreditsOrders from '~src/components/Removebg/CreditsOrders';
import { ChartRows, InsightCard, LoaderState } from './StoricoComponents';
import {
  createDefaultFilters,
  formatDurationFromMinutes,
  formatDurationFromSeconds,
  formatPercentage,
  formatShortDate,
  formatUsageCount,
  getBucketModeLabel,
  getFuneralServiceLabel,
  getPaginationItems,
  getPresetButtonLabel,
} from './StoricoHelpers';
import { FuneralServicesSelectorModal } from '~src/components/FuneralServices';
import {
  fetchAiCreditsOrders,
  fetchAllAiCreditsOrders,
} from '~graphql/controllers/fetchAiCreditsOrders';
import { fetchAllegatiByIds } from '~graphql/controllers/allegati';
import { fetchAllEditorProjectsConnection } from '~graphql/controllers/fetchEditorProjects';
import { onErrorShowMessage } from '~src/errors';
import {
  AI_CREDITS_PAGE_SIZE,
  AI_CREDITS_PAGE_SIZE_OPTIONS,
  AI_CREDITS_RANGE_PRESETS,
  AI_CREDITS_USED_BY_OPTIONS,
  buildAiCreditsGiottoProjectsWhere,
  buildAiCreditsHistoryInsights,
  buildAiCreditsOrdersWhere,
  formatAiCreditsAmount,
  getAiCreditOrderAttachmentId,
  getAiCreditsPresetRange,
} from '~src/utils/aiCreditsHistory';

const getInsightValues = (insights) => {
  const hasGiottoFuneralServiceCoverage =
    insights.giottoProjectFuneralServiceCount > 0;
  const hasGiottoProjectCoverage = insights.giottoProjectCount > 0;

  return {
    estimatedSavedTimeValue: formatDurationFromMinutes(
      insights.totalEstimatedSavedMinutes
    ),
    estimatedSavedTimeHint:
      insights.totalEstimatedSavedMinutes > 0
        ? `Con Giotto AI ci sono voluti circa ${formatDurationFromSeconds(
            insights.totalEstimatedAiSeconds
          )}`
        : 'Nessun utilizzo di Giotto AI nel periodo selezionato',
    averageUsedPerFuneralServiceValue:
      insights.funeralServiceCount > 0
        ? formatAiCreditsAmount(insights.averageUsedPerFuneralService)
        : '—',
    averageUsedPerFuneralServiceHint:
      insights.funeralServiceCount > 0
        ? `Media sui ${formatAiCreditsAmount(
            insights.funeralServiceCount
          )} defunti in cui hai usato AI`
        : 'Nessun defunto con AI nel periodo selezionato',
    giottoFuneralServiceCoverageValue: hasGiottoFuneralServiceCoverage
      ? formatPercentage(insights.aiGiottoProjectFuneralServiceCoverageRate)
      : '—',
    giottoFuneralServiceCoverageHint: hasGiottoFuneralServiceCoverage
      ? `${formatAiCreditsAmount(
          insights.aiGiottoProjectFuneralServiceCount
        )} defunti con AI su ${formatAiCreditsAmount(
          insights.giottoProjectFuneralServiceCount
        )} con progetto Giotto`
      : 'Nessun progetto Giotto nel periodo selezionato',
    giottoProjectCoverageValue: hasGiottoProjectCoverage
      ? formatPercentage(insights.aiGiottoProjectCoverageRate)
      : '—',
    giottoProjectCoverageHint: hasGiottoProjectCoverage
      ? `${formatAiCreditsAmount(
          insights.aiGiottoProjectCount
        )} progetti con AI su ${formatAiCreditsAmount(
          insights.giottoProjectCount
        )} progetti Giotto`
      : 'Nessun progetto Giotto nel periodo selezionato',
  };
};

const Storico = () => {
  const [filters, setFilters] = useState(createDefaultFilters);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(AI_CREDITS_PAGE_SIZE);
  const [orders, setOrders] = useState([]);
  const [summaryOrders, setSummaryOrders] = useState([]);
  const [giottoProjects, setGiottoProjects] = useState([]);
  const [aiAttachments, setAiAttachments] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loadingOrders, setLoadingOrders] = useState(true);
  const [loadingSummary, setLoadingSummary] = useState(true);
  const [
    showFuneralServiceSelectorModal,
    setShowFuneralServiceSelectorModal,
  ] = useState(false);

  useEffect(() => {
    let isMounted = true;
    const where = buildAiCreditsOrdersWhere(filters);

    setLoadingOrders(true);

    fetchAiCreditsOrders({
      limit: pageSize,
      skip: (page - 1) * pageSize,
      where,
    })
      .then((result) => {
        if (!isMounted) {
          return;
        }

        setOrders(result.edges || []);
        setTotalCount(result.count || 0);
        setLoadingOrders(false);
      })
      .catch((err) => {
        console.error(err);

        if (!isMounted) {
          return;
        }

        setOrders([]);
        setTotalCount(0);
        setLoadingOrders(false);
        onErrorShowMessage();
      });

    return () => {
      isMounted = false;
    };
  }, [filters, page, pageSize]);

  useEffect(() => {
    let isMounted = true;
    const ordersWhere = buildAiCreditsOrdersWhere(filters);
    const giottoProjectsWhere = buildAiCreditsGiottoProjectsWhere(filters);

    setLoadingSummary(true);

    Promise.all([
      fetchAllAiCreditsOrders({
        where: ordersWhere,
      }),
      fetchAllEditorProjectsConnection({
        where: giottoProjectsWhere,
      }),
    ])
      .then(async ([allOrders, allGiottoProjects]) => {
        const attachmentIds = Array.from(
          new Set(
            allOrders
              .map((order) => getAiCreditOrderAttachmentId(order))
              .filter(Boolean)
          )
        );
        const allAiAttachments = attachmentIds.length
          ? await fetchAllegatiByIds(attachmentIds)
          : [];

        if (!isMounted) {
          return;
        }

        setSummaryOrders(allOrders);
        setGiottoProjects(allGiottoProjects);
        setAiAttachments(allAiAttachments);
        setLoadingSummary(false);
      })
      .catch((err) => {
        console.error(err);

        if (!isMounted) {
          return;
        }

        setSummaryOrders([]);
        setGiottoProjects([]);
        setAiAttachments([]);
        setLoadingSummary(false);
        onErrorShowMessage();
      });

    return () => {
      isMounted = false;
    };
  }, [filters]);

  const insights = buildAiCreditsHistoryInsights(
    summaryOrders,
    filters,
    giottoProjects,
    aiAttachments
  );
  const insightValues = getInsightValues(insights);
  const totalPages = totalCount > 0 ? Math.ceil(totalCount / pageSize) : 1;
  const visiblePages = getPaginationItems(page, totalPages);
  const hasOrders = orders.length > 0;
  const isRefreshingOrders = loadingOrders && hasOrders;
  const appliedRangeText = `${formatShortDate(
    filters.dateFrom
  )} - ${formatShortDate(filters.dateTo)}`;
  const pageStart = totalCount === 0 ? 0 : (page - 1) * pageSize + 1;
  const pageEnd = totalCount === 0 ? 0 : Math.min(totalCount, page * pageSize);

  const updateFilter = (field, value) => {
    setFilters((currentFilters) => {
      const nextFilters = {
        ...currentFilters,
        [field]: value,
      };

      if (field === 'dateFrom' && value > currentFilters.dateTo) {
        nextFilters.dateTo = value;
      }

      if (field === 'dateTo' && value < currentFilters.dateFrom) {
        nextFilters.dateFrom = value;
      }

      if (field === 'dateFrom' || field === 'dateTo') {
        nextFilters.preset = 'custom';
      }

      return nextFilters;
    });
    setPage(1);
  };

  const handlePresetSelection = (preset) => {
    setFilters((currentFilters) => ({
      ...currentFilters,
      ...getAiCreditsPresetRange(preset),
      preset,
    }));
    setPage(1);
  };

  const handleResetFilters = () => {
    const defaultFilters = createDefaultFilters();
    setFilters(defaultFilters);
    setPage(1);
  };

  const handleSelectFuneralService = (service) => {
    if (!service?.objectId) {
      return;
    }

    setFilters((currentFilters) => ({
      ...currentFilters,
      funeralServiceId: service.objectId,
      funeralServiceName: getFuneralServiceLabel(service),
    }));
    setPage(1);
    setShowFuneralServiceSelectorModal(false);
  };

  return (
    <section className="ai-credits-history__section">
      <div className="ai-credits-history__header">
        <div>
          <p className="lead text-dark mb-1">Uso degli strumenti AI</p>
        </div>
        <div className="ai-credits-history__range-chip">
          <strong>{totalCount}</strong> risultati nel periodo {appliedRangeText}
        </div>
      </div>

      <div className="ai-credits-history__filters">
        <div className="ai-credits-history__presets">
          {AI_CREDITS_RANGE_PRESETS.map((preset) => (
            <Button
              key={preset.value}
              variant={
                filters.preset === preset.value ? 'primary' : 'outline-primary'
              }
              className="ai-credits-history__preset-btn"
              onClick={() => handlePresetSelection(preset.value)}
            >
              {getPresetButtonLabel(preset)}
            </Button>
          ))}
          <Button
            variant={
              filters.preset === 'custom' ? 'primary' : 'outline-primary'
            }
            className="ai-credits-history__preset-btn"
            onClick={() => updateFilter('preset', 'custom')}
          >
            Periodo personalizzato
          </Button>
        </div>

        <Row className="mt-3">
          <Col md={4}>
            <Form.Group controlId="aiCreditsMovementType">
              <Form.Label>Tipo</Form.Label>
              <Form.Control
                as="select"
                value={filters.movementType}
                onChange={(event) =>
                  updateFilter('movementType', event.target.value)
                }
              >
                <option value="all">Tutti</option>
                <option value="usage">Solo utilizzi</option>
                <option value="credit">Solo accrediti</option>
              </Form.Control>
            </Form.Group>
          </Col>

          <Col md={4}>
            <Form.Group controlId="aiCreditsUsedBy">
              <Form.Label>Strumento AI</Form.Label>
              <Form.Control
                as="select"
                value={filters.usedBy}
                onChange={(event) => updateFilter('usedBy', event.target.value)}
              >
                <option value="all">Tutti gli strumenti</option>
                {AI_CREDITS_USED_BY_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>
          </Col>

          <Col md={4}>
            <Form.Group controlId="aiCreditsFuneralService">
              <Form.Label>Defunto</Form.Label>
              <div className="ai-credits-history__selector-field">
                <Button
                  variant={
                    filters.funeralServiceId ? 'primary' : 'outline-primary'
                  }
                  className="btn-block"
                  onClick={() => setShowFuneralServiceSelectorModal(true)}
                >
                  {filters.funeralServiceName || 'Cerca defunto'}
                </Button>
                {filters.funeralServiceId ? (
                  <Button
                    variant="link"
                    className="ai-credits-history__selector-clear"
                    onClick={() => {
                      setFilters((currentFilters) => ({
                        ...currentFilters,
                        funeralServiceId: '',
                        funeralServiceName: '',
                      }));
                      setPage(1);
                    }}
                  >
                    Rimuovi filtro
                  </Button>
                ) : null}
              </div>
            </Form.Group>
          </Col>
        </Row>

        <Row>
          <Col md={3}>
            <Form.Group controlId="aiCreditsDateFrom">
              <Form.Label>Da</Form.Label>
              <Form.Control
                type="date"
                value={filters.dateFrom}
                onChange={(event) =>
                  updateFilter('dateFrom', event.target.value)
                }
              />
            </Form.Group>
          </Col>

          <Col md={3}>
            <Form.Group controlId="aiCreditsDateTo">
              <Form.Label>A</Form.Label>
              <Form.Control
                type="date"
                value={filters.dateTo}
                onChange={(event) => updateFilter('dateTo', event.target.value)}
              />
            </Form.Group>
          </Col>

          <Col md={6} className="d-flex align-items-end">
            <div className="ai-credits-history__filter-actions">
              <Button variant="outline-secondary" onClick={handleResetFilters}>
                Azzera filtri
              </Button>
            </div>
          </Col>
        </Row>
      </div>

      {loadingSummary ? (
        <LoaderState label="Sto preparando riepilogo e grafici..." />
      ) : (
        <>
          <Row className="ai-credits-history__kpis">
            <Col md={6} xl className="mb-3">
              <InsightCard
                label="Tempo risparmiato con Giotto AI"
                value={insightValues.estimatedSavedTimeValue}
                hint={insightValues.estimatedSavedTimeHint}
                tone="saved"
              />
            </Col>

            <Col md={6} xl className="mb-3">
              <InsightCard
                label="Crediti AI usati"
                value={formatAiCreditsAmount(insights.totalUsed)}
                hint={`${formatUsageCount(
                  insights.usageOrdersCount
                )} in cui hai usato i crediti AI`}
                tone="spent"
              />
            </Col>

            <Col md={6} xl className="mb-3">
              <InsightCard
                label="Crediti AI medi per defunto"
                value={insightValues.averageUsedPerFuneralServiceValue}
                hint={insightValues.averageUsedPerFuneralServiceHint}
                tone="average"
              />
            </Col>

            <Col md={6} xl className="mb-3">
              <InsightCard
                label="AI - Defunti"
                value={insightValues.giottoFuneralServiceCoverageValue}
                hint={insightValues.giottoFuneralServiceCoverageHint}
                tone="count"
              />
            </Col>

            <Col md={6} xl className="mb-3">
              <InsightCard
                label="AI - Progetti Giotto"
                value={insightValues.giottoProjectCoverageValue}
                hint={insightValues.giottoProjectCoverageHint}
                tone="added"
              />
            </Col>
          </Row>

          <Row className="mt-2">
            <Col lg={12} className="mb-3">
              <div className="ai-credits-history__chart-card">
                <div className="ai-credits-history__chart-header">
                  <div>
                    <strong>Crediti AI usati nel tempo</strong>
                    <p className="text-muted mb-0">
                      Vista {getBucketModeLabel(insights.bucketMode)} dei
                      crediti AI usati nel periodo.
                    </p>
                  </div>
                </div>

                <ChartRows
                  items={insights.timeline}
                  emptyLabel="Nessun utilizzo registrato nel range selezionato."
                  tone="timeline"
                  valueKey="total"
                  getValueLabel={(item) =>
                    `${formatAiCreditsAmount(item.total)} crediti`
                  }
                />
              </div>
            </Col>
          </Row>

          <Row>
            <Col lg={6} className="mb-3">
              <div className="ai-credits-history__chart-card">
                <div className="ai-credits-history__chart-header">
                  <div>
                    <strong>Crediti AI usati per strumento</strong>
                    <p className="text-muted mb-0">
                      Crediti AI usati in ogni strumento nel periodo.
                    </p>
                  </div>
                </div>

                <ChartRows
                  items={insights.tools}
                  emptyLabel="Nessun utilizzo disponibile per il filtro selezionato."
                  tone="context"
                  multiline
                  valueKey="total"
                  getValueLabel={(item) =>
                    `${formatAiCreditsAmount(item.total)} crediti`
                  }
                  getSecondaryLabel={(item) =>
                    `circa ${formatDurationFromMinutes(
                      item.estimatedSavedMinutes
                    )}`
                  }
                />
              </div>
            </Col>

            <Col lg={6} className="mb-3">
              <div className="ai-credits-history__chart-card">
                <div className="ai-credits-history__chart-header">
                  <div>
                    <strong>Defunti con piu crediti AI usati</strong>
                    <p className="text-muted mb-0">
                      Crediti AI usati per ciascun defunto.
                    </p>
                  </div>
                </div>

                <ChartRows
                  items={insights.funeralServices}
                  emptyLabel="Nessun defunto disponibile per il filtro selezionato."
                  tone="funeral"
                  multiline
                  valueKey="total"
                  getValueLabel={(item) =>
                    `${formatAiCreditsAmount(item.total)} crediti`
                  }
                />
              </div>
            </Col>
          </Row>
        </>
      )}

      <div className="ai-credits-history__table-section">
        <div className="ai-credits-history__table-header">
          <strong>Storico</strong>
          <p className="text-muted mb-0">
            Elenco paginato di utilizzi e accrediti dei crediti AI.
          </p>
        </div>

        {loadingOrders && !hasOrders ? (
          <LoaderState label="Sto caricando lo storico..." />
        ) : !hasOrders ? (
          <p className="ai-credits-history__empty-state">
            Nessun risultato trovato con i filtri selezionati.
          </p>
        ) : (
          <div className="ai-credits-history__table-shell">
            <div
              className={`ai-credits-history__table-content${
                isRefreshingOrders
                  ? ' ai-credits-history__table-content--loading'
                  : ''
              }`}
            >
              <CreditsOrders orders={orders} />
            </div>

            {isRefreshingOrders ? (
              <div className="ai-credits-history__table-overlay">
                <Spinner animation="border" variant="info" />
                <span>Aggiorno lo storico...</span>
              </div>
            ) : null}
          </div>
        )}

        {totalCount > 0 ? (
          <div className="ai-credits-history__pagination">
            <div className="ai-credits-history__pagination-meta">
              <span className="text-muted">
                Mostro {pageStart}-{pageEnd} di {totalCount} risultati
              </span>
              <span className="text-muted">
                Pagina {page} di {totalPages}
              </span>
              <Form.Group
                controlId="aiCreditsPageSize"
                className="ai-credits-history__pagination-size mb-0"
              >
                <Form.Label className="mb-0">Per pagina</Form.Label>
                <Form.Control
                  as="select"
                  value={pageSize}
                  disabled={loadingOrders}
                  onChange={(event) => {
                    setPageSize(Number(event.target.value));
                    setPage(1);
                  }}
                >
                  {AI_CREDITS_PAGE_SIZE_OPTIONS.map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </Form.Control>
              </Form.Group>
            </div>

            {totalPages > 1 ? (
              <Pagination className="mb-0 ai-credits-history__pagination-nav">
                <Pagination.Item
                  onClick={() => setPage(1)}
                  disabled={page === 1 || loadingOrders}
                >
                  Prima
                </Pagination.Item>
                <Pagination.Prev
                  onClick={() => setPage((currentPage) => currentPage - 1)}
                  disabled={page === 1 || loadingOrders}
                >
                  Indietro
                </Pagination.Prev>

                {visiblePages.map((visiblePage) =>
                  visiblePage.type === 'ellipsis' ? (
                    <Pagination.Ellipsis key={visiblePage.value} disabled />
                  ) : (
                    <Pagination.Item
                      key={visiblePage.value}
                      active={visiblePage.value === page}
                      disabled={loadingOrders}
                      onClick={() => setPage(visiblePage.value)}
                    >
                      {visiblePage.value}
                    </Pagination.Item>
                  )
                )}

                <Pagination.Next
                  onClick={() => setPage((currentPage) => currentPage + 1)}
                  disabled={page === totalPages || loadingOrders}
                >
                  Avanti
                </Pagination.Next>
                <Pagination.Item
                  onClick={() => setPage(totalPages)}
                  disabled={page === totalPages || loadingOrders}
                >
                  Ultima
                </Pagination.Item>
              </Pagination>
            ) : null}
          </div>
        ) : null}
      </div>

      <FuneralServicesSelectorModal
        show={showFuneralServiceSelectorModal}
        onHide={() => setShowFuneralServiceSelectorModal(false)}
        onChange={handleSelectFuneralService}
      />
    </section>
  );
};

export default Storico;
